import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeftRight,
  Plus,
  Calendar,
  Eye,
  Search,
  DollarSign,
  User,
  ArrowLeft,
  FileText,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Filter,
  ChevronDown,
  RefreshCw,
  Building2,
  X
} from 'lucide-react';
import { bondsAPI, investorAPI, subscriptionAPI, secondaryTransfersAPI, custodianAPI, walletAPI, formatCurrency, formatDate } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

// Hoisted CounterpartyWalletSelector component (was previously inline IIFE)
function CounterpartyWalletSelector({ counterpartyId, value, onChange }) {
  const { data, isLoading } = useQuery({
    queryKey: ['investor-counterparty-wallet', counterpartyId],
    enabled: !!counterpartyId,
    queryFn: async () => {
      try {
        const resp = await investorAPI.getCounterpartyWallet(counterpartyId);
        return resp?.data?.data || resp?.data || resp;
      } catch {
        return null;
      }
    },
    staleTime: 30000
  });
  const wallets = useMemo(() => {
    if (!data) return [];
    const arr = data?.wallets || (Array.isArray(data) ? data : [data]);
    return arr
      .filter(Boolean)
      .map(w => ({
        id: w._id || w.id,
        address: w.walletAddress || w.address || '',
        type: (w.bankAccountDetails?.accountType || '').toLowerCase()
      }));
  }, [data]);
  if (isLoading) return <div className="text-xs text-gray-500">Loading wallet...</div>;
  if (!wallets.length) return <div className="text-xs text-gray-400">No wallet data</div>;
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      className="w-full border border-gray-300 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
    >
      <option value="">Auto (primary)</option>
      {wallets.map(w => (
        <option key={w.id} value={w.id}>
          {w.type === 'islamic' ? 'Islamic' : 'Primary'}: {w.address.slice(0, 6)}...{w.address.slice(-4)}
        </option>
      ))}
    </select>
  );
}

const SecondaryTransfers = () => {
  const navigate = useNavigate();
  const { getDisplayName, user } = useAuth();
  // Classification rules:
  // - Sub-Account: user.isSubAccount === true (should inherit parent's capabilities INCLUDING initiation)
  // - Sub-Investor: any user with a truthy createdBy value (business rule: cannot initiate directly)
  // Primary investors and sub-accounts may initiate transfers; sub-investors cannot.
  const isSubAccount = !!user?.isSubAccount;
  // Sub-investor if createdBy is truthy (populated backend now always includes field)
  const isSubInvestor = !!user?.createdBy;
  // Show initiate button for any authenticated primary investor OR sub-account (exclude only sub-investors).
  const showInitiateButton = !!user && !isSubInvestor;
  // Approval actions hidden only for sub-investors; sub-accounts behave like primary investors
  const showApprovalActions = !!user && !isSubInvestor;
  const queryClient = useQueryClient();
  const [showTransferModal, setShowTransferModal] = useState(false);
  // View details modal state
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  // Basic validation state
  const [formErrors, setFormErrors] = useState({});
  // (Removed debug logging effects)

  // Transfer form state
  const [transferForm, setTransferForm] = useState({
    bondId: '',
    transferType: 'Sell',
    counterpartyId: '',
    counterpartyWalletId: '',
    nominalValue: '',
    tokenAmount: '',
    price: '',
    tradeDate: new Date().toISOString().split('T')[0],
    valueDate: '',
    paymentReference: '',
    transferReason: '',
    walletId: '' // investor wallet (source for Sell / receiving for Buy)
  });

  // Fetch investor wallets
  const { data: myWalletsData } = useQuery({
    queryKey: ['my-wallets', user?._id || user?.id],
    queryFn: async () => {
      try {
        const resp = await walletAPI.getMyWallets();
        // Attempt multiple known response shapes
        const raw = resp?.data?.data?.wallets
          || resp?.data?.wallets
          || resp?.data?.data
          || resp?.data
          || [];
        if (Array.isArray(raw)) return raw;
        // If raw is object with nested array values, pick first array found
        const firstArray = Object.values(raw || {}).find(v => Array.isArray(v));
        return Array.isArray(firstArray) ? firstArray : [];
      } catch (e) {
        console.error('Error fetching wallets', e);
        return [];
      }
    },
    enabled: !!(user?._id || user?.id)
  });

  const mappedWallets = useMemo(() => {
    const list = (myWalletsData || []).map(w => {
      const id = w._id || w.id || w.walletId;
      const accountTypeRaw = (w.bankAccountDetails?.accountType || w.accountType || w.type || '').toLowerCase();
      const accountType = accountTypeRaw === 'islamic' ? 'Islamic' : 'Conventional';
      const address = w.address || w.walletAddress || w.blockchainAddress || w.publicAddress || '';
      const short = address ? `${address.slice(0, 6)}...${address.slice(-4)}` : (id || 'WALLET');
      return {
        id,
        accountTypeRaw,
        accountType,
        address,
        label: `${short} (${accountType})`
      };
    });
    return list;
  }, [myWalletsData]);

  // Auto select if only one wallet
  useEffect(() => {
    if (mappedWallets.length === 1 && !transferForm.walletId) {
      setTransferForm(prev => ({ ...prev, walletId: mappedWallets[0].id }));
    }
  }, [mappedWallets, transferForm.walletId]);

  // Fetch investor's bond portfolio - real allocations where they have holdings
  const { data: portfolioData, isLoading: portfolioLoading } = useQuery({
    queryKey: ['investor-portfolio', user?.id],
    queryFn: async () => {
      try {
        // Get investor's allocations using the new dedicated endpoint
        const response = await subscriptionAPI.getMyAllocations({
          limit: 100 // Get all investor allocations
        });

        const allocations = response?.data?.data?.allocations || [];

        // Transform allocations (retain wallet reference, productType for filtering)
        const portfolioBonds = allocations
          .filter(allocation => allocation.tokenAmount > 0)
          .map(allocation => ({
            id: allocation.bondId,
            bondName: allocation.bondName,
            isin: allocation.isin,
            issuer: allocation.issuer,
            currentHolding: allocation.tokenAmount,
            availableForTransfer: allocation.tokenAmount,
            allocationPercentage: allocation.allocationPercentage,
            isWhitelisted: allocation.isWhitelisted,
            status: allocation.status,
            allocationType: allocation.allocationType,
            custodianName: allocation.custodianName,
            // Derive walletId strictly from allocation.walletAddress (backend schema uses walletAddress as Wallet ObjectId)
            walletId: (allocation.walletAddress && allocation.walletAddress._id) ? allocation.walletAddress._id : (allocation.walletAddress && allocation.walletAddress.id ? allocation.walletAddress.id : allocation.walletAddress),
            // Store chain/public address separately if populated
            walletAddress: allocation.walletAddress && allocation.walletAddress.walletAddress ? allocation.walletAddress.walletAddress : (allocation.walletChainAddress || null),
            productType: allocation.productType || allocation.bondProductType || allocation.bondType || allocation.type
          }));

        // Debug logs removed

        return portfolioBonds;
      } catch (error) {
        console.error('Error fetching investor allocations:', error);
        return [];
      }
    },
    enabled: !!user?.id
  });

  // When initiating a Buy transfer, fetch the selected counterparty's allocated bonds
  const { data: counterpartyBonds = [], isLoading: counterpartyBondsLoading } = useQuery({
    queryKey: ['counterparty-allocations', transferForm.transferType, transferForm.counterpartyId],
    queryFn: async () => {
      if (!transferForm.counterpartyId) return [];
      try {
        const data = await custodianAPI.getBondsByUserAllocations(transferForm.counterpartyId, {
          excludeMatured: true
        });
        // Support multiple backend shapes similar to custodian BondHoldings page
        let bonds = [];
        if (data && data.success && data.data && Array.isArray(data.data.bonds)) bonds = data.data.bonds;
        else if (Array.isArray(data)) bonds = data;
        else if (data && Array.isArray(data.data)) bonds = data.data;
        else if (data && data.bonds && Array.isArray(data.bonds)) bonds = data.bonds;
        const mapped = bonds.map(b => {
          const allocTotal = Number(b?.allocationInfo?.totalAllocationAmount || 0);
          return {
            id: b._id || b.id,
            bondName: b.bondName || 'Unnamed Bond',
            isin: b.isin || 'N/A',
            currentHolding: allocTotal, // explicit total allocation amount
            availableForTransfer: allocTotal, // assume fully transferable for now
            allocationPercentage: b.allocationInfo?.allocationPercentage,
            status: b.status,
            productType: b.productType || b.bondType || b.type
          };
        });
        // Debug logs removed
        return mapped;
      } catch (e) {
        console.error('Error fetching counterparty allocations:', e);
        return [];
      }
    },
    enabled: transferForm.transferType === 'Buy' && !!transferForm.counterpartyId
  });

  // Fetch secondary transfer requests involving this investor (server applies client/counterparty filter)
  const { data: transfersData, isLoading: transfersLoading } = useQuery({
    queryKey: ['investor-secondary-transfers', statusFilter, typeFilter, user?._id || user?.id],
    queryFn: () => secondaryTransfersAPI.getMyTransfers({
      status: statusFilter === 'all' ? undefined : statusFilter,
      transferType: typeFilter === 'all' ? undefined : typeFilter,
      limit: 100
    }),
    select: (resp) => {
      const payload = resp?.data?.data; // { transfers, pagination } expected
      const list = payload?.transfers || payload?.data || payload || [];
      if (!Array.isArray(list)) return [];
      const myId = user?._id || user?.id;
      return list.map(t => {
        const rawClientId = (typeof t.clientId === 'object') ? (t.clientId._id || t.clientId.id) : t.clientId;
        const rawCounterpartyId = (typeof t.counterpartyId === 'object') ? (t.counterpartyId._id || t.counterpartyId.id) : t.counterpartyId;
        // Support sub-account visibility: a sub-account should inherit parent's transfers
        const parentAccountId = user?.parentAccountId;
        const isSubAccount = user?.isSubAccount;
        // Treat as client if current user OR (if sub-account) their parent is the client
        const isClient = rawClientId === myId || (isSubAccount && parentAccountId && rawClientId === parentAccountId);
        // Direction from current user perspective
        // If transferType === 'Sell' and I'm the client -> Sell, else I'm the buyer => Buy. Vice versa for 'Buy'.
        let direction;
        if (t.transferType === 'Sell') direction = isClient ? 'Sell' : 'Buy';
        else if (t.transferType === 'Buy') direction = isClient ? 'Buy' : 'Sell';
        else direction = t.transferType || 'Sell';
        // Robust counterparty name derivation
        const extractName = (obj, fallback) => {
          if (!obj) return fallback;
          if (typeof obj === 'string') return fallback || obj;
          return obj.displayName || obj.fullName || obj.companyName || obj.name || obj.email || fallback || '';
        };
        const counterpartyName = isClient
          ? (t.counterpartyName || extractName(t.counterpartyId, rawCounterpartyId))
          : (t.clientName || extractName(t.clientId, rawClientId));
        return {
          id: t._id,
          transferRequestId: t.transferRequestId || t.requestId,
          // Data fields
          bondName: t.bondId?.bondName || t.bondName || 'Unknown Bond',
          isin: t.stockCode || t.bondId?.isin || t.isin || 'N/A',
          transferType: t.transferType,
          direction,
          counterpartyName,
          counterpartyId: isClient ? rawCounterpartyId : rawClientId,
          nominalValue: t.nominalValueTransferred,
          tokenAmount: t.tokenAmount || t.nominalValueTransferred,
          price: t.price,
          tradeDate: t.tradeDate,
          valueDate: t.valueDate,
          transferReason: t.transferReason || t.reason || '',
          paymentReference: t.paymentReference || t.paymentRef || '',
          status: t.status || t.approvalStatus || 'Pending Confirmation',
          approvalStatus: t.approvalStatus,
          l1ApprovedByName: t.l1ApprovedBy?.displayName || t.l1ApprovedBy?.fullName || t.l1ApprovedBy?.email || t.l1ApprovedBy?._id || '',
          l2ApprovedByName: t.l2ApprovedBy?.displayName || t.l2ApprovedBy?.fullName || t.l2ApprovedBy?.email || t.l2ApprovedBy?._id || '',
          l1ApprovedById: t.l1ApprovedBy?._id || t.l1ApprovedBy?.id || (typeof t.l1ApprovedBy === 'string' ? t.l1ApprovedBy : ''),
          l2ApprovedById: t.l2ApprovedBy?._id || t.l2ApprovedBy?.id || (typeof t.l2ApprovedBy === 'string' ? t.l2ApprovedBy : ''),
          // isClientInitiator now true for parent or its sub-account (so sub-account can approve)
          isClientInitiator: isClient
        };
      });
    },
    enabled: !!user?.id
  });

  // Fetch verified counterparties (direct investors and custodians), excluding sub-investors and self
  const { data: counterpartiesData } = useQuery({
    queryKey: ['investor-counterparties'],
    queryFn: async () => {
      const resp = await investorAPI.getCounterparties();
      return resp?.data?.data || [];
    },
    select: (list) => {
      return (list || [])
        .filter(u => (u.isDirectInvestor || u.isCustodian) && u.id !== (user?._id || user?.id))
        .map(u => ({
          id: u.id,
          name: u.name || u.email,
          type: u.isCustodian ? 'Custodian' : 'Investor',
          email: u.email
        }));
    },
    enabled: !!user?.id
  });

  // Filter transfers by search (status/type handled by server params)
  const filteredTransfers = useMemo(() => {
    if (!Array.isArray(transfersData)) return [];
    const term = searchTerm.toLowerCase();
    return transfersData.filter(t => {
      if (!term) return true;
      return [t.bondName, t.isin, t.counterpartyName].filter(Boolean).some(v => v.toLowerCase().includes(term));
    });
  }, [transfersData, searchTerm]);

  // Fetch full transfer detail when a transfer is selected for viewing
  const { data: transferDetailData, isLoading: transferDetailLoading } = useQuery({
    queryKey: ['secondary-transfer-detail', selectedTransfer?.id, selectedTransfer?.transferRequestId],
    queryFn: async () => {
      if (!selectedTransfer) return null;
      try {
        // Prefer ID endpoint; fallback to requestId if provided and different
        if (selectedTransfer.id) {
          const resp = await secondaryTransfersAPI.getTransfer(selectedTransfer.id);
          return resp?.data?.data || resp?.data || resp;
        } else if (selectedTransfer.transferRequestId) {
          const resp = await secondaryTransfersAPI.getTransferByRequestId(selectedTransfer.transferRequestId);
          return resp?.data?.data || resp?.data || resp;
        }
        return null;
      } catch (e) {
        console.error('Failed to load transfer detail', e);
        return null;
      }
    },
    enabled: showViewModal && !!selectedTransfer?.id,
    staleTime: 30_000
  });


  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Confirmation': { icon: Clock, bgColor: 'bg-yellow-100', textColor: 'text-yellow-800' },
      'Approved': { icon: CheckCircle, bgColor: 'bg-blue-100', textColor: 'text-blue-800' },
      'Processing': { icon: Clock, bgColor: 'bg-indigo-100', textColor: 'text-indigo-800' },
      'Completed': { icon: CheckCircle, bgColor: 'bg-green-100', textColor: 'text-green-800' },
      'Rejected': { icon: XCircle, bgColor: 'bg-red-100', textColor: 'text-red-800' },
      'Cancelled': { icon: XCircle, bgColor: 'bg-gray-100', textColor: 'text-gray-800' }
    };
    const config = statusConfig[status] || statusConfig['Pending Confirmation'];
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${config.bgColor} ${config.textColor}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  const getTransferTypeIcon = (type) => {
    return type === 'Sell' ? '📤' : '📥';
  };

  const handleInputChange = (field, value) => {
    setTransferForm(prev => {
      const updated = { ...prev, [field]: value };
      const errs = computeErrors(updated, selectedBondRef());
      setFormErrors(errs);
      return updated;
    });
  };

  // Determine which bonds to show based on transfer type (function declaration for hoisting)
  const selectedWallet = useMemo(() => mappedWallets.find(w => w.id === transferForm.walletId), [mappedWallets, transferForm.walletId]);
  const isIslamicSelectedWallet = () => selectedWallet?.accountTypeRaw === 'islamic';

  function getAvailableBonds() {
    // SELL: show investor's own bonds held in the selected wallet
    if (transferForm.transferType === 'Sell') {
      if (!transferForm.walletId) return [];
      const selWallet = selectedWallet;
      if (!selWallet) return [];
      const targetId = selWallet.id?.toString();
      return (portfolioData || []).filter(b => b.walletId && b.walletId.toString() === targetId);
    }
    // BUY: show counterparty's allocated bonds (independent of our wallet holdings)
    if (transferForm.transferType === 'Buy') {
      let list = counterpartyBonds || [];
      // If Islamic wallet selected, restrict to Sukuk products
      if (isIslamicSelectedWallet()) {
        list = list.filter(b => (b.productType || '').toLowerCase().includes('sukuk'));
      }
      return list;
    }
    return [];
  }

  // (Debug logging removed)

  // Get currently selected bond (hoisted)
  function getSelectedBond() {
    const availableBonds = getAvailableBonds();
    return availableBonds?.find(bond => bond.id?.toString() === transferForm.bondId);
  }

  const selectedBond = useMemo(() => getSelectedBond(), [transferForm.bondId, transferForm.transferType, portfolioData, counterpartyBonds]);
  const selectedBondRef = () => getSelectedBond();

  // Validation helpers
  const computeErrors = (form, selBond) => {
    const errors = {};
    const required = ['walletId', 'bondId', 'counterpartyId', 'nominalValue', 'tokenAmount', 'price', 'tradeDate', 'valueDate', 'paymentReference'];
    required.forEach(f => {
      if (!form[f] || form[f].toString().trim() === '') errors[f] = 'Required';
    });
    const nominal = parseFloat(form.nominalValue);
    if (form.nominalValue && (isNaN(nominal) || nominal <= 0)) errors.nominalValue = 'Must be > 0';
    if (!errors.nominalValue && selBond && !isNaN(nominal) && nominal > selBond.availableForTransfer) {
      errors.nominalValue = `Max ${selBond.availableForTransfer}`;
    }
    const tokens = parseInt(form.tokenAmount, 10);
    if (form.tokenAmount && (isNaN(tokens) || tokens <= 0)) errors.tokenAmount = 'Must be > 0';
    const price = parseFloat(form.price);
    if (form.price && (isNaN(price) || price <= 0)) errors.price = 'Must be > 0';
    if (form.tradeDate && form.valueDate) {
      if (new Date(form.valueDate) < new Date(form.tradeDate)) errors.valueDate = 'Must be >= trade date';
    }
    // Wallet/Bond compatibility (Islamic wallet only for Sukuk). Conventional can hold both.
    if (form.walletId && selBond) {
      const wallet = mappedWallets.find(w => w.id === form.walletId);
      const isIslamicWallet = wallet?.accountTypeRaw === 'islamic';
      const isIslamicBond = (selBond.productType || '').toLowerCase().includes('sukuk');
      if (isIslamicWallet && !isIslamicBond) {
        errors.bondId = 'Islamic wallet can only be used with Sukuk bonds';
      }
    }
    return errors;
  };
  const isFormValid = Object.keys(formErrors).length === 0;

  const handleSubmitTransfer = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    // Final validation check
    const finalErrors = computeErrors(transferForm, selectedBond);
    if (Object.keys(finalErrors).length) {
      setFormErrors(finalErrors);
      setIsSubmitting(false);
      return;
    }
    // Redundant guard (defensive) for wallet/bond mismatch
    if (transferForm.walletId && selectedBond) {
      const wallet = mappedWallets.find(w => w.id === transferForm.walletId);
      const isIslamicWallet = wallet?.accountTypeRaw === 'islamic';
      const isIslamicBond = (selectedBond.productType || '').toLowerCase().includes('sukuk');
      if (isIslamicWallet && !isIslamicBond) {
        alert('Islamic wallet can only be used with Sukuk bonds. Please choose a Sukuk bond or select a conventional wallet.');
        setIsSubmitting(false);
        return;
      }
    }
    const myId = user?._id || user?.id;
    const payload = {
      bondId: transferForm.bondId,
      transferType: transferForm.transferType === 'Sell' ? 'Sell' : 'Buy',
      clientId: myId,
      counterpartyId: transferForm.counterpartyId,
      counterpartyWalletId: transferForm.counterpartyWalletId || undefined,
      nominalValueTransferred: parseFloat(transferForm.nominalValue),
      price: parseFloat(transferForm.price),
      tradeDate: new Date(transferForm.tradeDate).toISOString(),
      valueDate: new Date(transferForm.valueDate).toISOString(),
      currency: 'MYR',
      transferReason: transferForm.transferReason || `${transferForm.transferType} secondary transfer`,
      paymentReference: (transferForm.paymentReference || '').trim(),
      clientWalletId: transferForm.walletId
    };
    try {
      await secondaryTransfersAPI.createTransfer(payload);
      queryClient.invalidateQueries(['investor-secondary-transfers']);
      setShowTransferModal(false);
      setTransferForm(prev => ({
        bondId: '',
        transferType: 'Sell',
        counterpartyId: '',
        counterpartyWalletId: '',
        nominalValue: '',
        tokenAmount: '',
        price: '',
        tradeDate: new Date().toISOString().split('T')[0],
        valueDate: '',
        paymentReference: '',
        transferReason: '',
        walletId: mappedWallets.length === 1 ? mappedWallets[0].id : ''
      }));
      alert('Secondary transfer request created. It will proceed after approvals.');
    } catch (err) {
      console.error('Create secondary transfer failed:', err);
      alert(err?.response?.data?.message || err.message || 'Failed to create secondary transfer');
    } finally { setIsSubmitting(false); }
  };

  // ===== Approvals (dual-layer) =====
  const approveTransferMutation = useMutation({
    mutationFn: ({ id, notes }) => secondaryTransfersAPI.approveTransfer(id, { approvalNotes: notes }),
    onSuccess: () => {
      queryClient.invalidateQueries(['investor-secondary-transfers']);
    }
  });

  const handleApprove = (transfer, level) => {
    if (!transfer) return;
    // Determine phase
    if (level === 'L1' && (transfer.approvalStatus === 'L1 Approved' || transfer.approvalStatus === 'L2 Approved')) return;
    if (level === 'L2' && transfer.approvalStatus !== 'L1 Approved') return; // cannot L2 before L1
    approveTransferMutation.mutate({ id: transfer.id, notes: `${level} approval via Investor UI` });
  };

  // (moved getSelectedBond & getAvailableBonds above for hoisting)

  // Get loading state for bond selection
  const getBondSelectionLoading = () => portfolioLoading;
  // Enhanced loading reflecting Buy vs Sell
  const bondSelectionLoading = transferForm.transferType === 'Sell' ? portfolioLoading : counterpartyBondsLoading;

  // Reset bond selection when transfer type changes
  const handleTransferTypeChange = (newType) => {
    handleInputChange('transferType', newType);
    // Clear bond selection when transfer type changes
    if (transferForm.bondId) handleInputChange('bondId', '');
    handleInputChange('counterpartyId', '');
  };

  // Auto clear incompatible bond when Islamic wallet selected in Buy mode
  useEffect(() => {
    if (transferForm.transferType !== 'Buy') return;
    if (!transferForm.bondId) return;
    if (!isIslamicSelectedWallet()) return;
    const sel = getSelectedBond();
    if (sel && !((sel.productType || '').toLowerCase().includes('sukuk'))) {
      setTransferForm(prev => ({ ...prev, bondId: '' }));
    }
  }, [transferForm.walletId, transferForm.bondId, transferForm.transferType]);

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setTypeFilter('all');
  };

  if (portfolioLoading || transfersLoading) return <LoadingSpinner />;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* CounterpartyWalletSelector hoisted above; removed inline IIFE definition */}
      {/* Header */}
      <div >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Secondary Transfers</h1>
              <p className="text-sm text-gray-600 mt-1">
                Initiate and manage secondary market transfers for your bond holdings
              </p>
            </div>
            <div className="flex items-center space-x-4">
              {showInitiateButton && (
                <button
                  onClick={() => setShowTransferModal(true)}
                  className="btn-primary"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Initiate New Transfer
                </button>
              )}

            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search and Filters */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 mb-6">
          {/* Search Card - 60% width (3 out of 5 columns) */}
          <div className="card lg:col-span-3">
            <div className="card-body">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-dark-gray w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search by Bond Name, ISIN, or Counterparty..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input pl-10"
                />
              </div>
            </div>
          </div>

          {/* Status Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Statuses</option>
                <option value="Pending Confirmation">Pending Confirmation</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Rejected">Rejected</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Type Filter Card - 20% width (1 out of 5 columns) */}
          <div className="card lg:col-span-1">
            <div className="card-body">
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="form-input"
              >
                <option value="all">All Types</option>
                <option value="Sell">Sell</option>
                <option value="Buy">Buy</option>
              </select>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Showing {filteredTransfers.length} of {transfersData?.length || 0} secondary transfer requests
          </p>
        </div>

        {/* Transfer Requests Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden overflow-x-auto">
          <div className="overflow-x-auto">
            <div className="card-header">
              <div className="flex items-center justify-between w-full">
                <h3>Secondary Transfer Requests</h3>

              </div>
            </div>
            <table className="table">
              <thead className="table-header">
                <tr>
                  <th className="table-header-cell">Request ID</th>
                  <th className="table-header-cell">
                    Bond (ISIN)
                  </th>
                  <th className="table-header-cell">
                    Transfer Type
                  </th>
                  <th className="table-header-cell">
                    Counterparty
                  </th>
                  <th className="table-header-cell">
                    Nominal Value
                  </th>
                  <th className="table-header-cell">Price</th>
                  <th className="table-header-cell">
                    Trade Date
                  </th>
                  <th className="table-header-cell">
                    Value Date
                  </th>
                  <th className="table-header-cell">L1 Approved By</th>
                  <th className="table-header-cell">L2 Approved By</th>
                  <th className="table-header-cell">
                    Status
                  </th>
                  <th className="table-header-cell">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-light-gray-200">
                {filteredTransfers.length === 0 ? (
                  <tr>
                    <td colSpan="12" className="px-6 py-12 text-center text-gray-500">
                      <ArrowLeftRight className="mx-auto h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-lg font-medium">No transfer requests found</p>
                      <p className="text-sm">Try adjusting your search or filter criteria</p>
                    </td>
                  </tr>
                ) : (
                  filteredTransfers.map((t) => (
                    <tr key={t.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{t.transferRequestId || t.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{t.bondName}</div>
                        <div className="text-xs text-gray-500 font-mono">{t.isin}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">

                          <span className="text-sm text-gray-900">{t.direction}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.counterpartyName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(t.nominalValue)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(t.price)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(t.tradeDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(t.valueDate)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.l1ApprovedByName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{t.l2ApprovedByName || '-'}</td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(t.status)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {showApprovalActions && t.isClientInitiator && !['Completed', 'Rejected', 'Cancelled'].includes(t.status) && t.approvalStatus !== 'L2 Approved' && (
                            (() => {
                              const phase = t.approvalStatus;
                              const currentUserId = user?._id || user?.id;
                              const sameApprover = t.l1ApprovedById && t.l1ApprovedById === currentUserId;
                              const isL1Done = phase === 'L1 Approved';
                              const isL2Done = phase === 'L2 Approved';
                              // Diagnostic reason builder
                              let disableReason = '';
                              if (!isL1Done && !isL2Done) {
                                disableReason = approveTransferMutation.isLoading ? 'Submitting L1...' : '';
                              } else if (isL1Done && !isL2Done && sameApprover) {
                                disableReason = 'Same user performed L1; different account required for L2';
                              } else if (isL1Done && !isL2Done && approveTransferMutation.isLoading) {
                                disableReason = 'Submitting L2...';
                              }
                              // One-off debug log (can be removed later)
                              // Row debug logs removed
                              if (!phase || (phase !== 'L1 Approved' && phase !== 'L2 Approved')) {
                                return (
                                  <button
                                    onClick={() => handleApprove(t, 'L1')}
                                    disabled={approveTransferMutation.isLoading}
                                    className="btn-primary btn-sm"
                                    title={approveTransferMutation.isLoading ? 'Submitting...' : 'Approve L1'}
                                  >
                                    {approveTransferMutation.isLoading ? '...' : 'Approve L1'}
                                  </button>
                                );
                              }
                              if (phase === 'L1 Approved' && phase !== 'L2 Approved') {
                                return (
                                  <button
                                    onClick={() => !sameApprover && handleApprove(t, 'L2')}
                                    disabled={approveTransferMutation.isLoading || sameApprover}
                                    title={disableReason || (sameApprover ? 'Different user required for L2 approval' : 'Approve L2')}
                                    className="btn-primary btn-sm"
                                  >
                                    {approveTransferMutation.isLoading ? '...' : 'Approve L2'}
                                  </button>
                                );
                              }
                              return null;
                            })()
                          )}
                          <button
                            onClick={() => { setSelectedTransfer(t); setShowViewModal(true); }}
                            className="text-primary-600 hover:text-primary-700"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Initiate Transfer Modal */}
      {showTransferModal && showInitiateButton && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-medium text-gray-900">
                Initiate New Secondary Transfer
              </h3>
              <button
                onClick={() => setShowTransferModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            <div className="space-y-6">
              {/* Transfer Type Selection - Now first */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Type *
                </label>
                <div className="flex space-x-4">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="Sell"
                      checked={transferForm.transferType === 'Sell'}
                      onChange={(e) => handleTransferTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Sell (I am selling my bonds)</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      value="Buy"
                      checked={transferForm.transferType === 'Buy'}
                      onChange={(e) => handleTransferTypeChange(e.target.value)}
                      className="mr-2"
                    />
                    <span className="text-sm">Buy (I am buying bonds from counterparty)</span>
                  </label>
                </div>
              </div>

              {/* Wallet Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Wallet *</label>
                <select
                  value={transferForm.walletId}
                  onChange={(e) => {
                    handleInputChange('walletId', e.target.value);
                    if (transferForm.bondId) handleInputChange('bondId', '');
                  }}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                >
                  <option value="">Select wallet (source / receiving)</option>
                  {mappedWallets.map(w => (
                    <option key={w.id} value={w.id}>{w.label}</option>
                  ))}
                </select>
                {selectedWallet && (
                  <div className="mt-2 p-2 bg-blue-50 rounded border text-xs text-blue-800">
                    Account Type: {selectedWallet.accountType} {isIslamicSelectedWallet() && '(Only Sukuk purchases allowed)'}
                  </div>
                )}
                {formErrors.walletId && <p className="mt-1 text-xs text-red-600">{formErrors.walletId}</p>}
              </div>

              {/* Counterparty Selection - Now second for Buy transfers */}
              {transferForm.transferType === 'Buy' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Counterparty (Seller) *
                  </label>
                  <select
                    value={transferForm.counterpartyId}
                    onChange={(e) => {
                      handleInputChange('counterpartyId', e.target.value);
                      // Clear bond selection when counterparty changes
                      if (transferForm.bondId) {
                        handleInputChange('bondId', '');
                      }
                      handleInputChange('counterpartyWalletId', '');
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    required
                  >
                    <option value="">Select the seller (who owns the bonds you want to buy)</option>
                    {counterpartiesData?.map(party => (
                      <option key={party.id} value={party.id}>
                        {party.name} ({party.type})
                      </option>
                    ))}
                  </select>
                  {transferForm.counterpartyId && (
                    <div className="mt-2">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Counterparty Wallet (optional)</label>
                      <CounterpartyWalletSelector
                        counterpartyId={transferForm.counterpartyId}
                        value={transferForm.counterpartyWalletId}
                        onChange={(val) => handleInputChange('counterpartyWalletId', val)}
                      />
                    </div>
                  )}
                  <div className="mt-2 p-2 bg-blue-50 rounded border text-xs text-blue-800">
                    💡 Select the counterparty first to see their available bonds for purchase
                  </div>
                </div>
              )}

              {/* Bond Selection - Now conditional based on transfer type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bond Selection *
                  </label>

                  {bondSelectionLoading ? (
                    <div className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm text-gray-500">
                      Loading available bonds...
                    </div>
                  ) : (
                    <select
                      value={transferForm.bondId}
                      onChange={(e) => handleInputChange('bondId', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                      disabled={!transferForm.walletId || (transferForm.transferType === 'Buy' && !transferForm.counterpartyId)}
                    >
                      <option value="">
                        {(() => {
                          if (transferForm.transferType === 'Sell') {
                            const availableBonds = getAvailableBonds();
                            if (!transferForm.walletId) return 'Select wallet first';
                            return availableBonds && availableBonds.length > 0
                              ? 'Select a bond from this wallet to sell'
                              : 'No bonds in this wallet available';
                          } else if (transferForm.transferType === 'Buy') {
                            if (!transferForm.counterpartyId) {
                              return "Select a counterparty first";
                            }
                            const availableBonds = getAvailableBonds();
                            if (!transferForm.walletId) return 'Select wallet first';
                            return availableBonds && availableBonds.length > 0
                              ? (isIslamicSelectedWallet() ? 'Select Sukuk bond to buy' : 'Select a bond to buy from counterparty')
                              : (isIslamicSelectedWallet() ? 'No Sukuk bonds available from counterparty' : 'Counterparty has no allocated bonds');
                          }
                          return "Select transfer type first";
                        })()}
                      </option>
                      {getAvailableBonds()?.map(bond => {
                        const suffix = bond.productType ? ` | ${bond.productType}` : '';
                        return (
                          <option key={bond.id + (bond.walletAddress || '')} value={bond.id}>
                            {bond.bondName} ({bond.isin}) - {formatCurrency(bond.currentHolding)} avail{suffix}
                          </option>
                        );
                      })}
                    </select>
                  )}

                  {/* Information messages based on transfer type */}
                  {transferForm.transferType === 'Sell' && transferForm.walletId && getAvailableBonds() && getAvailableBonds().length === 0 && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded border text-xs text-yellow-800">
                      You don't have any bond allocations available for sale. Bonds must be allocated and have positive holdings to be transferable.
                    </div>
                  )}

                  {transferForm.transferType === 'Buy' && transferForm.counterpartyId && transferForm.walletId && !bondSelectionLoading && getAvailableBonds().length === 0 && (
                    <div className="mt-2 p-2 bg-yellow-50 rounded border text-xs text-yellow-800">
                      {isIslamicSelectedWallet() ? 'The selected counterparty currently has no Sukuk bonds available.' : 'The selected counterparty currently has no allocated bonds to display.'}
                    </div>
                  )}

                  {getSelectedBond() && (
                    <div className="mt-2 p-2 bg-blue-50 rounded border text-xs text-blue-800">
                      {transferForm.transferType === 'Sell' ? 'Available to sell' : 'Available to buy'}: {formatCurrency(getSelectedBond().availableForTransfer)}
                    </div>
                  )}
                </div>

                {/* Counterparty for Sell transfers */}
                {transferForm.transferType === 'Sell' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Counterparty (Buyer) *
                    </label>
                    <select
                      value={transferForm.counterpartyId}
                      onChange={(e) => handleInputChange('counterpartyId', e.target.value)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      required
                    >
                      <option value="">Select the buyer</option>
                      {counterpartiesData?.map(party => (
                        <option key={party.id} value={party.id}>
                          {party.name} ({party.type})
                        </option>
                      ))}
                    </select>
                    {transferForm.counterpartyId && (
                      <div className="mt-2">
                        <label className="block text-xs font-medium text-gray-600 mb-1">Buyer Wallet (optional)</label>
                        <CounterpartyWalletSelector
                          counterpartyId={transferForm.counterpartyId}
                          value={transferForm.counterpartyWalletId}
                          onChange={(val) => handleInputChange('counterpartyWalletId', val)}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Transaction Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nominal Value Transferred *
                  </label>
                  <input
                    type="number"
                    value={transferForm.nominalValue}
                    onChange={(e) => handleInputChange('nominalValue', e.target.value)}
                    placeholder="Enter nominal value"
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.nominalValue ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.nominalValue && <p className="mt-1 text-xs text-red-600">{formErrors.nominalValue}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Bond Amount *
                  </label>
                  <input
                    type="number"
                    value={transferForm.tokenAmount}
                    onChange={(e) => handleInputChange('tokenAmount', e.target.value)}
                    placeholder="Enter number of tokens"
                    min="1"
                    step="1"
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.tokenAmount ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.tokenAmount && <p className="mt-1 text-xs text-red-600">{formErrors.tokenAmount}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price *
                  </label>
                  <input
                    type="number"
                    value={transferForm.price}
                    onChange={(e) => handleInputChange('price', e.target.value)}
                    placeholder="Agreed-upon price"
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.price ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.price && <p className="mt-1 text-xs text-red-600">{formErrors.price}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Trade Date *
                  </label>
                  <input
                    type="date"
                    value={transferForm.tradeDate}
                    onChange={(e) => handleInputChange('tradeDate', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.tradeDate ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.tradeDate && <p className="mt-1 text-xs text-red-600">{formErrors.tradeDate}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Value Date (Settlement Date) *
                  </label>
                  <input
                    type="date"
                    value={transferForm.valueDate}
                    onChange={(e) => handleInputChange('valueDate', e.target.value)}
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.valueDate ? 'border-red-500' : 'border-gray-300'}`}
                  />
                  {formErrors.valueDate && <p className="mt-1 text-xs text-red-600">{formErrors.valueDate}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Payment Reference *
                  </label>
                  <input
                    type="text"
                    value={transferForm.paymentReference}
                    onChange={(e) => handleInputChange('paymentReference', e.target.value)}
                    placeholder="Reference for off-chain cash settlement"
                    className={`w-full border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${formErrors.paymentReference ? 'border-red-500' : 'border-gray-300'}`}
                    required
                  />
                  {formErrors.paymentReference && <p className="mt-1 text-xs text-red-600">{formErrors.paymentReference}</p>}
                </div>
              </div>

              {/* Transfer Reason */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Transfer Reason (Optional)
                </label>
                <textarea
                  value={transferForm.transferReason}
                  onChange={(e) => handleInputChange('transferReason', e.target.value)}
                  rows={3}
                  placeholder="Brief description of the reason for transfer (e.g., 'OTC Sale', 'Inter-company Transfer')"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Confirmation */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="confirm-accuracy"
                  className="mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  required
                />
                <label htmlFor="confirm-accuracy" className="text-sm text-gray-700">
                  I confirm that the above information is accurate and I authorize this transfer request.
                </label>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => setShowTransferModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitTransfer}
                disabled={!isFormValid || isSubmitting}
                className="px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:cursor-not-allowed bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Transfer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Transfer Detail Modal (Enhanced) */}
      {showViewModal && selectedTransfer && (() => {
        const detail = transferDetailData?.data || transferDetailData || {};
        const merged = { ...selectedTransfer, ...detail };
        const nominalValue = merged.nominalValueTransferred ?? merged.nominalValue;
        const priceVal = merged.price ?? 0;
        const totalValue = (typeof nominalValue === 'number' && typeof priceVal === 'number') ? nominalValue * priceVal : null;
        const isSell = (merged.direction || merged.transferType) === 'Sell';
        const partyALabel = isSell ? 'Seller (You)' : 'Buyer (You)';
        const partyBLabel = isSell ? 'Buyer (Counterparty)' : 'Seller (Counterparty)';
        const displayCounterpartyId = (() => {
          const v = merged.counterpartyId;
          if (!v) return '-';
          if (typeof v === 'string' || typeof v === 'number') return v;
          if (typeof v === 'object') {
            return v._id || v.id || v.email || v.displayName || '[object]';
          }
          return '-';
        })();
        return (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[90vh] overflow-y-auto shadow-xl">
              <div className="p-6 pb-0">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Secondary Transfer Details</h3>
                  <button onClick={() => { setShowViewModal(false); setSelectedTransfer(null); }} className="h-6 w-6" style={{ color: '#9ca3af' }}>
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>
              <div className="border-b border-light-gray w-full -mx-0 mt-4"></div>
              <div className="p-6 pt-6">
                {transferDetailLoading && (
                  <div className="text-center text-sm text-gray-500 mb-4">Loading full transfer data...</div>
                )}
                {/* Summary Cards */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{getTransferTypeIcon(merged.direction || merged.transferType)}</span>
                      <div>
                        <h4 className="text-lg font-semibold text-gray-900">Transfer Request</h4>
                        <p className="text-sm text-gray-600">Request ID: {merged.transferRequestId || merged.requestId || merged._id || merged.id}</p>
                      </div>
                    </div>
                    <div className="text-right">{getStatusBadge(merged.status)}</div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="text-center p-3 bg-white rounded border">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Nominal Value</p>
                      <p className="text-lg font-semibold text-gray-900">{nominalValue != null ? formatCurrency(nominalValue) : '-'}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Price</p>
                      <p className="text-lg font-semibold text-gray-900">{priceVal != null ? formatCurrency(priceVal) : '-'}</p>
                    </div>
                    <div className="text-center p-3 bg-white rounded border">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Total Value</p>
                      <p className="text-lg font-semibold text-green-600">{totalValue != null ? formatCurrency(totalValue) : '-'}</p>
                    </div>
                  </div>
                </div>
                {/* Parties */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div className="bg-blue-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-blue-900 mb-3">{partyALabel}</h5>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-blue-700">Account</p>
                        <p className="text-sm font-medium text-blue-900">You / Your Account</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4">
                    <h5 className="text-sm font-medium text-green-900 mb-3">{partyBLabel}</h5>
                    <div className="space-y-2">
                      <div>
                        <p className="text-xs text-green-700">Name</p>
                        <p className="text-sm font-medium text-green-900">{merged.counterpartyName}</p>
                      </div>
                      <div>
                        <p className="text-xs text-green-700">Counterparty ID</p>
                        <p className="text-sm font-mono text-green-800">{displayCounterpartyId}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Bond Info */}
                <div className="bg-purple-50 rounded-lg p-4 mb-6">
                  <h5 className="text-sm font-medium text-purple-900 mb-3">Bond Information</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs text-purple-700">Bond Name</p>
                      <p className="text-sm font-medium text-purple-900">{merged.bondId?.bondName || merged.bondName}</p>
                    </div>
                    <div>
                      <p className="text-xs text-purple-700">ISIN</p>
                      <p className="text-sm font-mono text-purple-800">{merged.bondId?.isin || merged.isin}</p>
                    </div>
                  </div>
                </div>
                {/* Dates & Additional Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Important Dates</h5>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Trade Date</span>
                        <span className="text-sm font-medium text-gray-900">{merged.tradeDate ? formatDate(merged.tradeDate) : '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Value Date</span>
                        <span className="text-sm font-medium text-gray-900">{merged.valueDate ? formatDate(merged.valueDate) : '-'}</span>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium text-gray-900 mb-3">Approval & Metadata</h5>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Status</span>
                        <span className="text-sm font-medium text-gray-900">{merged.status}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">Approval Status</span>
                        <span className="text-sm font-medium text-gray-900">{merged.approvalStatus || merged.status}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">L1 Approved By</span>
                        <span className="text-sm font-medium text-gray-900">{merged.l1ApprovedBy?.displayName || merged.l1ApprovedByName || '-'}</span>
                      </div>
                      <div className="flex justify-between items-center py-2 border-b border-gray-200">
                        <span className="text-sm text-gray-600">L2 Approved By</span>
                        <span className="text-sm font-medium text-gray-900">{merged.l2ApprovedBy?.displayName || merged.l2ApprovedByName || '-'}</span>
                      </div>
                      {merged.paymentReference && (
                        <div className="flex justify-between items-center py-2 border-b border-gray-200">
                          <span className="text-sm text-gray-600">Payment Reference</span>
                          <span className="text-sm font-mono text-gray-900">{merged.paymentReference}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {/* Transfer Reason */}
                {merged.transferReason && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-900 mb-2">Transfer Reason</h5>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <p className="text-sm text-gray-700">{merged.transferReason}</p>
                    </div>
                  </div>
                )}
                <div className="flex justify-end pt-4">
                  <button onClick={() => { setShowViewModal(false); setSelectedTransfer(null); }} className="btn-primary">Close</button>
                </div>
              </div>
            </div>
          </div>
        );
      })()}


    </div>
  );
};

export default SecondaryTransfers;